import React from 'react';
import * as THREE from 'three';
import { useThree, invalidate, ThreeEvent } from '@react-three/fiber';
import { ClippingBounds } from '../clippingBounds';
import { Model } from '../../hooks/useModelState';

interface ClippingBoxHandlesProps {
  model: Model;
  bounds: ClippingBounds;
  modelOffset: THREE.Vector3;
  modelScale: number;
}

interface FaceDef {
  boundKey: keyof ClippingBounds;
  threeAxis: 'x' | 'y' | 'z';
  direction: THREE.Vector3;
  getFacePosition: (box: THREE.Box3, center: THREE.Vector3) => THREE.Vector3;
}

const FACES: FaceDef[] = [
  {
    boundKey: 'minX',
    threeAxis: 'x',
    direction: new THREE.Vector3(1, 0, 0),
    getFacePosition: (box, c) => new THREE.Vector3(box.max.x, c.y, c.z),
  },
  {
    boundKey: 'maxX',
    threeAxis: 'x',
    direction: new THREE.Vector3(-1, 0, 0),
    getFacePosition: (box, c) => new THREE.Vector3(box.min.x, c.y, c.z),
  },
  {
    boundKey: 'maxZ',
    threeAxis: 'y',
    direction: new THREE.Vector3(0, 1, 0),
    getFacePosition: (box, c) => new THREE.Vector3(c.x, box.max.y, c.z),
  },
  {
    boundKey: 'minZ',
    threeAxis: 'y',
    direction: new THREE.Vector3(0, -1, 0),
    getFacePosition: (box, c) => new THREE.Vector3(c.x, box.min.y, c.z),
  },
  {
    boundKey: 'maxY',
    threeAxis: 'z',
    direction: new THREE.Vector3(0, 0, 1),
    getFacePosition: (box, c) => new THREE.Vector3(c.x, c.y, box.max.z),
  },
  {
    boundKey: 'minY',
    threeAxis: 'z',
    direction: new THREE.Vector3(0, 0, -1),
    getFacePosition: (box, c) => new THREE.Vector3(c.x, c.y, box.min.z),
  },
];

const UP = new THREE.Vector3(0, 1, 0);

const VERT = 'void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }';
const FRAG = 'uniform vec3 uColor; void main() { gl_FragColor = vec4(uColor, 1.0); }';

const NORMAL_MATERIAL = new THREE.ShaderMaterial({
  uniforms: { uColor: { value: new THREE.Color('#ffaa00') } },
  vertexShader: VERT,
  fragmentShader: FRAG,
  depthTest: false,
});

const HOVER_MATERIAL = new THREE.ShaderMaterial({
  uniforms: { uColor: { value: new THREE.Color('#ffffff') } },
  vertexShader: VERT,
  fragmentShader: FRAG,
  depthTest: false,
});

const worldToCadBound = (
  worldValue: number,
  face: FaceDef,
  offset: THREE.Vector3,
  scale: number,
): number => {
  switch (face.threeAxis) {
    case 'x': return -(worldValue / scale - offset.x);
    case 'y': return worldValue / scale - offset.y;
    case 'z': return worldValue / scale - offset.z;
    default: return 0;
  }
};

const buildBox = (bounds: ClippingBounds, offset: THREE.Vector3, scale: number): THREE.Box3 => new THREE.Box3(
  new THREE.Vector3(
    (-bounds.maxX + offset.x) * scale,
    (bounds.minZ + offset.y) * scale,
    (bounds.minY + offset.z) * scale,
  ),
  new THREE.Vector3(
    (-bounds.minX + offset.x) * scale,
    (bounds.maxZ + offset.y) * scale,
    (bounds.maxY + offset.z) * scale,
  ),
);

const ClippingBoxHandles = ({ model, bounds, modelOffset, modelScale }: ClippingBoxHandlesProps) => {
  const { camera, controls, gl } = useThree();
  const [hoveredFace, setHoveredFace] = React.useState<string | null>(null);
  const isDragging = React.useRef(false);
  const modelRef = React.useRef(model);
  modelRef.current = model;

  const box = React.useMemo(() => buildBox(bounds, modelOffset, modelScale), [bounds, modelOffset, modelScale]);
  const boxCenter = React.useMemo(() => box.getCenter(new THREE.Vector3()), [box]);
  const arrowSize = modelScale * 2.5;

  const handlePointerDown = React.useCallback((face: FaceDef, e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();

    const currentBounds = modelRef.current.clippingBounds;
    if (!currentBounds) return;

    isDragging.current = true;
    const worldPos: THREE.Vector3 = e.point.clone();

    const camDir = new THREE.Vector3();
    camera.getWorldDirection(camDir);
    const axis = face.direction;
    const cross1 = new THREE.Vector3().crossVectors(camDir, axis);
    const planeNormal = new THREE.Vector3().crossVectors(axis, cross1);
    if (planeNormal.lengthSq() < 0.0001) {
      planeNormal.copy(camDir);
    }
    planeNormal.normalize();
    const dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, worldPos);

    if (controls) (controls as unknown as { enabled: boolean }).enabled = false;

    const onMove = (me: PointerEvent) => {
      const rect = gl.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((me.clientX - rect.left) / rect.width) * 2 - 1,
        -((me.clientY - rect.top) / rect.height) * 2 + 1,
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);

      const intersection = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(dragPlane, intersection)) return;

      const axisValue = intersection[face.threeAxis as keyof THREE.Vector3] as number;
      const newCadValue = worldToCadBound(axisValue, face, modelOffset, modelScale);

      const latestBounds = modelRef.current.clippingBounds;
      if (!latestBounds) return;

      const newBounds = { ...latestBounds, [face.boundKey]: newCadValue };
      if (newBounds.minX > newBounds.maxX || newBounds.minY > newBounds.maxY || newBounds.minZ > newBounds.maxZ) return;

      modelRef.current.setClippingBounds(newBounds);
      invalidate();
    };

    const onUp = () => {
      isDragging.current = false;
      if (controls) (controls as unknown as { enabled: boolean }).enabled = true;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      invalidate();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [camera, controls, gl, modelOffset, modelScale]);

  return (
    <>
      {FACES.map((face) => {
        const facePos = face.getFacePosition(box, boxCenter);
        const nudge = face.direction.clone().multiplyScalar(arrowSize);
        const pos = facePos.clone().add(nudge);
        const rotation = new THREE.Euler().setFromQuaternion(
          new THREE.Quaternion().setFromUnitVectors(UP, face.direction),
        );
        const isHovered = hoveredFace === face.boundKey;

        return (
          <mesh
            key={face.boundKey}
            position={pos}
            rotation={rotation}
            renderOrder={999}
            material={isHovered ? HOVER_MATERIAL : NORMAL_MATERIAL}
            onPointerDown={(e) => handlePointerDown(face, e)}
            onPointerOver={(e) => {
              e.stopPropagation();
              if (!isDragging.current) {
                setHoveredFace(face.boundKey);
                gl.domElement.style.cursor = 'grab';
              }
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              if (!isDragging.current) {
                setHoveredFace(null);
                gl.domElement.style.cursor = 'auto';
              }
            }}
          >
            <coneGeometry args={[arrowSize, arrowSize * 2, 8]} />
          </mesh>
        );
      })}
    </>
  );
};

export default ClippingBoxHandles;
